import axios from "axios";
import TokenService from "@/services/tokenService";
import { useAuthStore } from "@/stores/auth";
import * as Sentry from '@sentry/vue';

const BASE_API = import.meta.env.VITE_APP_BASE_URL;
const GATEWAY_URL = import.meta.env.VITE_APP_GATEWAY_URL;
const PDF_API = import.meta.env.VITE_PDF_APP_BASE_URL;

const getLanguageHeader = () =>
  localStorage.getItem("locale")?.toUpperCase() || "EN";

const createAxiosInstance = (baseURL: string) => {
  return axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": getLanguageHeader(),
    },
  });
};

const instance = createAxiosInstance(BASE_API);
const pdfInstance = createAxiosInstance(PDF_API);
const authApiInstance = createAxiosInstance(GATEWAY_URL);

instance.interceptors.request.use(
  (config) => {
    const token = TokenService.getLocalAccessToken();
    if (token) {
      // @ts-ignore
      config.headers = {
        ...config.headers,
        authorization: `Bearer ${token}`,
        "X-ID-TOKEN": localStorage.getItem("idToken"),
        "X-COMPANY-ID": localStorage.getItem("companyId"),
      };
    }

    const activeSpan = Sentry.getActiveSpan();
    if (activeSpan) {

      config.headers['sentry-trace'] = Sentry.spanToTraceHeader(activeSpan);


      const baggageHeader = Sentry.spanToBaggageHeader(activeSpan);
      if (baggageHeader) {
        config.headers['baggage'] = baggageHeader;
      }

    }

    const user = Sentry.getCurrentScope().getUser();

    if (user?.id) {
      config.headers['X-User-ID'] = user.id;
    } else {
      const userStore = useAuthStore();
      if (userStore.user) {
        config.headers['X-User-ID'] = userStore.user.id;
      }
    }

    // Ekstra bilgiler
    config.headers['X-Request-Timestamp'] = Date.now();

    return config;
  },
  (error) => {
    Sentry.captureException(error);
    return Promise.reject(error)
  }
);

let isRefreshing = false;
let failedQueue: { resolve: (value: any) => void; reject: (reason?: any) => void; config: any }[] = [];

const processQueue = async (error: any, token: string | null) => {
  while (failedQueue.length > 0) {
    const prom = failedQueue.shift();

    if (!prom) continue;

    if (token) {
      try {
        const response = await axios({
          ...prom.config,
          headers: {
            ...prom.config.headers,
            authorization: `Bearer ${token}`,
            "X-ID-TOKEN": localStorage.getItem("idToken"),
            "X-COMPANY-ID": localStorage.getItem("companyId"),
          },
        });
        prom.resolve(response);
      } catch (err) {
        prom.reject(err);
      }
    } else {
      prom.reject(error);
    }
  }
};

instance.interceptors.response.use(
  (res) => res,
  async (err) => {

    const originalConfig = err.config;
    if (err.response?.status === 401 && !originalConfig._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalConfig });
        });
      }

      originalConfig._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${GATEWAY_URL}/auth/refresh-token`, {
          refreshToken: TokenService.getLocalRefreshToken(),
        });

        localStorage.setItem("token", data.accessToken);
        localStorage.setItem("idToken", data.idToken);

        instance.defaults.headers.authorization = `Bearer ${data.accessToken}`;

        await processQueue(null, data.accessToken);

        originalConfig.headers.authorization = `Bearer ${data.accessToken}`;
        return axios(originalConfig);
      } catch (_error) {
        await processQueue(_error, null);
        await useAuthStore().logout();
        return Promise.reject(_error);
      } finally {
        isRefreshing = false;
      }
    }


    Sentry.withScope((scope) => {
      scope.setExtras({
        response_data: err.response?.data,
        request_url: err.config?.url,
        request_method: err.config?.method,
        status_code: err.response?.status,
      });
      Sentry.captureException(err);
    });

    return Promise.reject(err);
  }
);

export { pdfInstance, authApiInstance };
export default instance;
